// Apolo IDE — Plan tab canvas math helpers (sprint plan-tab-v1, E2).
//
// Pan/zoom/snap/intersection — pure functions, no React. Tests can hit
// these directly and components stay declarative.

export const ZOOM_MIN  = 0.2;
export const ZOOM_MAX  = 3;
export const ZOOM_STEP = 0.1;
export const SNAP_GRID = 8; // px in canvas-space

export function clampZoom(z) {
  if (!Number.isFinite(z)) return 1;
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z));
}

export function snap(value, grid = SNAP_GRID) {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value / grid) * grid;
}

/**
 * Convert a screen-space (client) point to canvas-space, given the current
 * viewport translation (panX/panY in screen pixels) and zoom factor.
 */
export function screenToCanvas({ x, y, panX, panY, zoom }) {
  return {
    x: (x - panX) / zoom,
    y: (y - panY) / zoom,
  };
}

/**
 * Inverse: given a canvas-space point, return where it lands on the screen.
 */
export function canvasToScreen({ x, y, panX, panY, zoom }) {
  return {
    x: x * zoom + panX,
    y: y * zoom + panY,
  };
}

/**
 * Given an existing zoom + new zoom + the screen pivot (mouse position),
 * compute the new pan so the pivot point stays anchored on screen.
 */
export function zoomAround({ pivotX, pivotY, currentZoom, nextZoom, panX, panY }) {
  const z0 = currentZoom;
  const z1 = clampZoom(nextZoom);
  // Canvas point under pivot before zoom:
  const cx = (pivotX - panX) / z0;
  const cy = (pivotY - panY) / z0;
  // New pan so the same canvas point lands under the pivot after zoom:
  const newPanX = pivotX - cx * z1;
  const newPanY = pivotY - cy * z1;
  return { zoom: z1, panX: newPanX, panY: newPanY };
}

/**
 * Compute bounding box of an array of blocks ({x,y,w,h}). Returns null
 * when empty.
 */
export function blocksBoundingBox(blocks) {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const b of blocks) {
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.w);
    maxY = Math.max(maxY, b.y + b.h);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/**
 * Compute pan + zoom that fits a bounding box into the given viewport,
 * with a small padding (in screen pixels).
 */
export function fitToView({ bbox, viewportW, viewportH, padding = 64 }) {
  if (!bbox || bbox.w <= 0 || bbox.h <= 0) {
    return { zoom: 1, panX: viewportW / 2, panY: viewportH / 2 };
  }
  const availW = Math.max(50, viewportW - padding * 2);
  const availH = Math.max(50, viewportH - padding * 2);
  const zoom = clampZoom(Math.min(availW / bbox.w, availH / bbox.h));
  const centerX = bbox.x + bbox.w / 2;
  const centerY = bbox.y + bbox.h / 2;
  const panX = viewportW / 2 - centerX * zoom;
  const panY = viewportH / 2 - centerY * zoom;
  return { zoom, panX, panY };
}

/**
 * Connection path between two blocks' centers, as an SVG cubic bezier
 * "M x0,y0 C cx0,cy0 cx1,cy1 x1,y1" string. Curve bows outward depending
 * on horizontal distance.
 */
export function connectionPath(fromBlock, toBlock) {
  if (!fromBlock || !toBlock) return '';
  const x0 = fromBlock.x + fromBlock.w / 2;
  const y0 = fromBlock.y + fromBlock.h / 2;
  const x1 = toBlock.x + toBlock.w / 2;
  const y1 = toBlock.y + toBlock.h / 2;
  const dx = Math.abs(x1 - x0);
  const bow = Math.min(200, dx * 0.4);
  const c0x = x0 + bow;
  const c1x = x1 - bow;
  return `M ${x0},${y0} C ${c0x},${y0} ${c1x},${y1} ${x1},${y1}`;
}
